import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import loadEcharts from "../functions/echartsLoader";
import { buildLineOption, safeNum } from "../functions/sectionReportCalculations";
import "../views/style/analytics.css";
import "../views/style/companySearch.css";

const SNAPSHOT_URL = "/data/alza-27082440-snapshot.json";
const START_YEAR = 2016;

const INDUSTRY_RENTABILITY_SERIES = [
  { name: "ROA", key: "ROA__median", percent: true },
  { name: "ROE", key: "ROE__median", percent: true },
  { name: "EBITDA marže", key: "EBITDA marže__median", percent: true },
];

const METRIC_COLORS = {
  REVENUE: "#91cc75",
  EBITDA: "#5470c6",
  PROFIT: "#fac858",
  DEFAULT: "#73c0de",
};

const normalizePercent = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return num;
};

const safeNumStrict = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeRatio = (value) => {
  const num = safeNumStrict(value);
  if (!Number.isFinite(num)) return null;
  return Math.abs(num) > 1 ? num / 100 : num;
};

const getFirstNumericValue = (row, keys) => {
  if (!row) return null;
  for (const key of keys) {
    const value = safeNumStrict(row[key]);
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const buildConnectedLineOption = (years, series, formatter) => {
  const option = buildLineOption(years, series, formatter);
  option.series = option.series.map((item) => ({ ...item, connectNulls: true }));
  return option;
};

const formatMaybeNumber = (value) => (Number.isFinite(value) ? value : "-");
const formatCurrency = (value) => {
  const numeric = safeNumStrict(value);
  return Number.isFinite(numeric) ? `${numeric.toLocaleString("cs-CZ")}` : "-";
};
const formatPercent = (value) => (Number.isFinite(value) ? `${value.toFixed(1)}%` : "-");
const formatMilKcFromThousands = (value) => {
  const numeric = safeNumStrict(value);
  if (!Number.isFinite(numeric)) return "-";
  const milValue = numeric / 1000;
  const absValue = Math.abs(milValue);
  const fractionDigits = absValue < 100 ? 1 : 0;
  const formatted = milValue.toLocaleString("cs-CZ", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return `${formatted} mil. Kč`;
};

const CompanyReportSnapshot = ({ expandRequested = false, onExpandedChange }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState("loading");
  const [isExpanded, setIsExpanded] = useState(false);
  const revenueChartRef = useRef(null);
  const profitabilityChartRef = useRef(null);
  const activityIndexRef = useRef(null);
  const industryRentabilityRef = useRef(null);
  const companyAssetsChartRef = useRef(null);
  const industryAssetsChartRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    setStatus("loading");
    fetch(SNAPSHOT_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!isMounted) return;
        setSnapshot(data);
        setStatus("done");
      })
      .catch((err) => {
        console.error("Snapshot load failed", err);
        if (isMounted) setStatus("error");
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const contextData = snapshot?.context;
  const industryReport = snapshot?.industry_report;
  const summaryText = snapshot?.summary;

  const financials = useMemo(() => {
    if (!contextData?.financni_ukazatele) return [];
    return [...contextData.financni_ukazatele].sort((a, b) => a.Rok - b.Rok);
  }, [contextData]);

  const startYear = useMemo(() => {
    if (!financials.length) return START_YEAR;
    return Math.min(...financials.map((row) => row.Rok));
  }, [financials]);

  const years = useMemo(() => {
    if (!financials.length) return [];
    const endYear = Math.max(...financials.map((row) => row.Rok));
    return Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
  }, [financials, startYear]);

  const benchYearsLabel = useMemo(() => {
    const yearsArr = contextData?.bench_years;
    if (!yearsArr || !Array.isArray(yearsArr) || yearsArr.length === 0) {
      return "";
    }
    const minYear = Math.min(...yearsArr);
    const maxYear = Math.max(...yearsArr);

    if (minYear === maxYear) {
      return `(${minYear})`;
    }
    return `(${minYear}–${maxYear})`;
  }, [contextData]);

  const summaryMetrics = useMemo(() => {
    if (!financials.length) return null;
    const last = financials[financials.length - 1];
    const analysis = contextData?.bench_result?.analysis || {};

    const cagrKey = Object.keys(analysis).find((key) => key.toLowerCase().includes("trzby_cagr") || key === "trzby_cagr");
    const revenueCagr = cagrKey ? analysis[cagrKey].company_value : null;

    const roaKey = Object.keys(analysis).find((key) => key.toLowerCase().includes("roa"));
    const roa = roaKey ? normalizePercent(analysis[roaKey].company_value) : null;

    const roeKey = Object.keys(analysis).find((key) => key.toLowerCase().includes("roe"));
    const roe = roeKey ? normalizePercent(analysis[roeKey].company_value) : null;

    const ebitdaMargin = normalizePercent(last["EBITDA marže (%)"]);

    return {
      latestYear: last.Rok,
      revenueCagr,
      ebitdaMargin,
      roa,
      roe,
      revenueLatest: safeNum(last["Tržby"]),
      ebitdaLatest: safeNum(last["EBITDA"]),
      profitLatest: safeNum(last["Zisk po zdanění"]),
    };
  }, [financials, contextData]);

  const valuationRangeText = useMemo(() => {
    const range = contextData?.valuation_result?.estimated_value_range;
    if (!range) return "-";

    const lowRaw = safeNumStrict(range.low);
    const highRaw = safeNumStrict(range.high);

    const hasLow = Number.isFinite(lowRaw);
    const hasHigh = Number.isFinite(highRaw);

    if (!hasLow && !hasHigh) return "-";

    const low = hasLow ? Math.round(lowRaw / 1000) : null;
    const high = hasHigh ? Math.round(highRaw / 1000) : null;

    if (hasLow && hasHigh) {
      return `${formatCurrency(low)} – ${formatCurrency(high)} mil. Kč`;
    }

    const singleValue = hasLow ? formatCurrency(low) : formatCurrency(high);
    return `${singleValue} mil. Kč`;
  }, [contextData]);

  const formatBenchmarkValue = (metric, value) => {
    const numeric = safeNumStrict(value);
    if (!Number.isFinite(numeric)) return value ?? "-";

    const lower = metric.toLowerCase();

    if (lower.includes("obrat")) {
      return numeric.toLocaleString("cs-CZ", { maximumFractionDigits: 0 });
    }

    if (lower.includes("likvidita")) {
      return numeric.toFixed(2);
    }

    if (
      lower.includes("roa") ||
      lower.includes("roe") ||
      lower.includes("ros") ||
      lower.includes("marže") ||
      lower.includes("marze") ||
      lower.includes("margin") ||
      lower.includes("zadluženost") ||
      lower.includes("cagr") ||
      lower.includes("%")
    ) {
      return `${(numeric * 100).toFixed(1)}%`;
    }

    return numeric.toLocaleString("cs-CZ");
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("cs-CZ");
  };

  const normalizePercentile = (value) => {
    const numeric = safeNumStrict(value);
    if (!Number.isFinite(numeric)) return null;
    return Math.abs(numeric) <= 1 ? numeric * 100 : numeric;
  };

  const getPercentileClass = (value) => {
    if (!Number.isFinite(value)) return "";
    if (value >= 75) return "company-search__percentile--high";
    if (value >= 40) return "company-search__percentile--mid";
    return "company-search__percentile--low";
  };

  const benchmarkRows = useMemo(() => {
    const analysis = contextData?.bench_result?.analysis;
    if (!analysis) return [];
    return Object.entries(analysis)
      .map(([metric, info]) => ({
        metric,
        companyValue: info.company_value,
        median: info.median,
        percentile: info.percentile,
        text: info.text,
        status: info.status,
      }))
      .sort((a, b) => {
        const aIsCagr = b.metric?.toLowerCase().includes("cagr");
        const bIsCagr = a.metric?.toLowerCase().includes("cagr");
        if (aIsCagr && !bIsCagr) return -1;
        if (!aIsCagr && bIsCagr) return 1;
        return a.metric.localeCompare(b.metric, "cs");
      });
  }, [contextData]);

  const additionalInfoRows = useMemo(() => {
    if (!contextData?.zakladni_udaje) return [];
    const details = contextData.zakladni_udaje;
    return [
      { label: "Právní forma", value: details.pravni_forma || "-" },
      { label: "Vlastnictví", value: details.vlastnictvi || "-" },
      { label: "Status subjektu", value: details.status || "-" },
      { label: "Adresa", value: details.adresa || "-" },
      { label: "Plátce DPH", value: details.je_platce_dph ? "Ano" : "Ne" },
      { label: "Datum založení", value: formatDate(details.datum_zalozeni) },
      { label: "Datum účetní závěrky", value: formatDate(details.datum_uzaverky) },
      { label: "Zveřejnění závěrky", value: formatDate(details.datum_zverejneni_uzaverky) },
      { label: "Web", value: details.web || "-" },
    ];
  }, [contextData]);

  const financialTableRows = useMemo(() => {
    if (!financials.length) return [];
    return [...financials].sort((a, b) => b.Rok - a.Rok);
  }, [financials]);

  const revenueSeries = useMemo(() => {
    if (!financials.length) return null;
    return {
      years,
      series: [
        {
          name: "Tržby",
          itemStyle: { color: METRIC_COLORS.REVENUE },
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? safeNumStrict(row["Tržby"]) : null;
          }),
        },
        {
          name: "EBITDA",
          itemStyle: { color: METRIC_COLORS.EBITDA },
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? safeNumStrict(row["EBITDA"]) : null;
          }),
        },
        {
          name: "Zisk po zdanění",
          itemStyle: { color: METRIC_COLORS.PROFIT },
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? safeNumStrict(row["Zisk po zdanění"]) : null;
          }),
        },
      ],
    };
  }, [financials, years]);

  const profitabilitySeries = useMemo(() => {
    if (!financials.length) return null;
    return {
      years,
      series: [
        {
          name: "ROA",
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? normalizePercent(row["ROA (%)"]) : null;
          }),
        },
        {
          name: "ROE",
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? normalizePercent(row["ROE (%)"]) : null;
          }),
        },
        {
          name: "EBITDA marže",
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? normalizePercent(row["EBITDA marže (%)"]) : null;
          }),
        },
      ],
    };
  }, [financials, years]);

  const activityIndexSeries = useMemo(() => {
    const indices = industryReport?.indices;
    if (!indices?.length) return null;

    const windows = Array.from(new Set(indices.map((row) => row.window)));
    const targetWindow = `${START_YEAR}_${years[years.length - 1]}`;
    const window = windows.includes(targetWindow) ? targetWindow : windows.includes("2016_2025") ? "2016_2025" : windows[0];

    const filteredIndices = indices.filter((row) => row.window === window);

    const revenueRows = filteredIndices.filter((row) => row.metric.toUpperCase().includes("TRŽBY"));
    if (!revenueRows.length) return null;

    const history = industryReport?.financial_ratios?.history || [];
    const periodAll = industryReport?.financial_ratios?.period_all?.[0] || null;
    const marginRows = history.length ? history : years.map((year) => ({ Year: year, ...periodAll }));
    const marginKey = "EBITDA marže__median";

    return {
      years,
      window,
      series: [
        {
          name: "Tržby (index)",
          itemStyle: { color: METRIC_COLORS.REVENUE },
          data: years.map((year) => {
            const row = revenueRows.find((item) => item.Year === year);
            return row ? safeNumStrict(row.index_median) : null;
          }),
        },
        {
          name: "EBITDA (index)",
          itemStyle: { color: METRIC_COLORS.EBITDA },
          data: years.map((year) => {
            const revRow = revenueRows.find((item) => item.Year === year);
            const revenue = revRow ? safeNumStrict(revRow.index_median) : null;

            const marginRow = marginRows.find((item) => item.Year === year);
            const rawMargin = marginRow ? marginRow[marginKey] : null;
            const marginRatio = normalizeRatio(rawMargin);

            if (!Number.isFinite(revenue) || !Number.isFinite(marginRatio)) return null;

            return {
              value: revenue * marginRatio,
              margin: Number.isFinite(rawMargin) ? rawMargin : marginRatio * 100,
            };
          }),
        },
      ],
    };
  }, [industryReport, years]);

  const industryRentabilitySeries = useMemo(() => {
    const history = industryReport?.financial_ratios?.history || [];
    const periodAll = industryReport?.financial_ratios?.period_all?.[0] || null;
    if (!history.length && !periodAll) return null;

    const rows = history.length ? history : years.map((year) => ({ Year: year, ...periodAll }));

    return {
      years,
      series: INDUSTRY_RENTABILITY_SERIES.map((def) => ({
        name: def.name,
        data: years.map((year) => {
          const row = rows.find((item) => item.Year === year);
          if (!row) return null;
          const val = def.percent ? normalizePercent(row[def.key]) : safeNum(row[def.key]);
          return def.percent && val !== null ? val * 100 : val;
        }),
      })),
    };
  }, [industryReport, years]);

  const companyAssetsDebtSeries = useMemo(() => {
    if (!financials.length) return null;
    const assetKeys = ["Aktiva celkem", "Aktiva", "Celková aktiva", "AKTIVA CELKEM"];
    const debtKeys = ["Zadluženost celková (%)"];

    return {
      years,
      series: [
        {
          name: "Aktiva",
          itemStyle: { color: METRIC_COLORS.REVENUE },
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return getFirstNumericValue(row, assetKeys);
          }),
        },
        {
          name: "Cizí zdroje",
          itemStyle: { color: METRIC_COLORS.PROFIT },
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            if (!row) return null;

            const assets = getFirstNumericValue(row, assetKeys);
            const rawRatio = getFirstNumericValue(row, debtKeys);
            const ratio = normalizeRatio(rawRatio);

            if (!Number.isFinite(assets) || !Number.isFinite(ratio)) return null;

            return {
              value: assets * ratio,
              ratio: Number.isFinite(rawRatio) ? rawRatio : ratio * 100,
            };
          }),
        },
      ],
    };
  }, [financials, years]);

  const industryAssetsDebtSeries = useMemo(() => {
    const indices = industryReport?.indices || [];
    if (!indices.length) return null;

    const windows = Array.from(new Set(indices.map((row) => row.window)));
    const targetWindow = `${START_YEAR}_${years[years.length - 1]}`;
    const window = windows.includes(targetWindow) ? targetWindow : windows.includes("2016_2025") ? "2016_2025" : windows[0];

    const filtered = indices.filter((row) => row.window === window);
    if (!filtered.length) return null;

    const assetsRows = filtered.filter((row) => row.metric?.toUpperCase().includes("AKTIVA CELKEM"));
    if (!assetsRows.length) return null;

    const history = industryReport?.financial_ratios?.history || [];
    const periodAll = industryReport?.financial_ratios?.period_all?.[0] || null;
    const ratioRows = history.length ? history : years.map((year) => ({ Year: year, ...periodAll }));

    const ratioKeys = [
      "Zadluženost celková__median",
      "Zadluženost celková",
      "Zadluzenost celkova",
      "Zadluženost celková (%)",
    ];

    return {
      years,
      series: [
        {
          name: "Aktiva celkem (index)",
          itemStyle: { color: METRIC_COLORS.REVENUE },
          data: years.map((year) => {
            const row = assetsRows.find((item) => item.Year === year);
            return row ? safeNumStrict(row.index_median) : null;
          }),
        },
        {
          name: "Zadluženost celková (přepočet)",
          itemStyle: { color: METRIC_COLORS.PROFIT },
          data: years.map((year) => {
            const assetRow = assetsRows.find((item) => item.Year === year);
            const assets = assetRow ? safeNumStrict(assetRow.index_median) : null;

            const ratioRow = ratioRows.find((item) => item.Year === year);
            const rawRatio = getFirstNumericValue(ratioRow, ratioKeys);
            const ratio = normalizeRatio(rawRatio);

            if (!Number.isFinite(assets) || !Number.isFinite(ratio)) return null;

            return {
              value: assets * ratio,
              realValue: ratio * 100,
            };
          }),
        },
      ],
      window,
    };
  }, [industryReport, years]);

  useEffect(() => {
    if (!isExpanded) return;
    if (!financials.length && !industryReport) return;
    let charts = [];
    let handleResize;
    loadEcharts()
      .then((echarts) => {
        const renderChart = (ref, option) => {
          if (!ref.current || !option) return;
          const chart = echarts.getInstanceByDom(ref.current) || echarts.init(ref.current);
          chart.setOption(option);
          charts.push(chart);
        };

        if (revenueSeries) {
          renderChart(
            revenueChartRef,
            buildConnectedLineOption(revenueSeries.years, revenueSeries.series, (value) =>
              Number.isFinite(value) ? value.toLocaleString("cs-CZ") : "—"
            )
          );
        }

        if (profitabilitySeries) {
          renderChart(
            profitabilityChartRef,
            buildConnectedLineOption(profitabilitySeries.years, profitabilitySeries.series, (value) =>
              Number.isFinite(value) ? `${value.toFixed(1)}%` : "—"
            )
          );
        }

        if (activityIndexSeries) {
          const option = buildConnectedLineOption(
            activityIndexSeries.years,
            activityIndexSeries.series,
            (value) => (Number.isFinite(value) ? value.toLocaleString("cs-CZ") : "—")
          );

          option.tooltip.formatter = (params) => {
            let result = `<strong>${params[0].axisValue}</strong><br/>`;

            params.forEach((param) => {
              const marker = param.marker;
              const seriesName = param.seriesName;
              const data = param.data;

              const val = data && typeof data === "object" && "value" in data ? data.value : data;
              const isSmallNumber = Number.isFinite(val) && Math.abs(val) < 100;

              let displayStr = Number.isFinite(val)
                ? val.toLocaleString("cs-CZ", {
                    minimumFractionDigits: isSmallNumber ? 2 : 0,
                    maximumFractionDigits: isSmallNumber ? 2 : 0,
                  })
                : "-";

              result += `${marker} ${seriesName}: <strong>${displayStr}</strong><br/>`;
            });
            return result;
          };

          renderChart(activityIndexRef, option);
        }

        if (industryRentabilitySeries) {
          renderChart(
            industryRentabilityRef,
            buildConnectedLineOption(industryRentabilitySeries.years, industryRentabilitySeries.series, (value) =>
              Number.isFinite(value) ? `${value.toFixed(1)}%` : "—"
            )
          );
        }

        if (companyAssetsDebtSeries) {
          const option = buildConnectedLineOption(
            companyAssetsDebtSeries.years,
            companyAssetsDebtSeries.series,
            (value) => (Number.isFinite(value) ? value.toLocaleString("cs-CZ") : "—")
          );

          option.tooltip.formatter = (params) => {
            let result = `<strong>${params[0].axisValue}</strong><br/>`;

            params.forEach((param) => {
              const marker = param.marker;
              const seriesName = param.seriesName;
              const data = param.data;

              const val = data && typeof data === "object" && "value" in data ? data.value : data;

              let displayStr = Number.isFinite(val) ? val.toLocaleString("cs-CZ", { maximumFractionDigits: 0 }) : "-";

              if (data && typeof data === "object" && "ratio" in data && Number.isFinite(data.ratio)) {
                displayStr += ` (${data.ratio.toFixed(1)} %)`;
              }

              result += `${marker} ${seriesName}: <strong>${displayStr}</strong><br/>`;
            });
            return result;
          };

          renderChart(companyAssetsChartRef, option);
        }

        if (industryAssetsDebtSeries) {
          const option = buildConnectedLineOption(
            industryAssetsDebtSeries.years,
            industryAssetsDebtSeries.series,
            (value) => (Number.isFinite(value) ? value.toLocaleString("cs-CZ") : "—")
          );

          option.tooltip.formatter = (params) => {
            let result = `<strong>${params[0].axisValue}</strong><br/>`;

            params.forEach((param) => {
              const marker = param.marker;
              const seriesName = param.seriesName;
              const data = param.data;

              let displayValue;

              if (data && typeof data === "object" && "realValue" in data) {
                displayValue = `${data.realValue.toFixed(1)} %`;
              } else {
                const val = data && typeof data === "object" ? data.value : data;
                displayValue = Number.isFinite(val) ? val.toLocaleString("cs-CZ") : "-";
              }

              result += `${marker} ${seriesName}: <strong>${displayValue}</strong><br/>`;
            });
            return result;
          };

          renderChart(industryAssetsChartRef, option);
        }

        handleResize = () => {
          charts.forEach((chart) => chart.resize());
        };
        window.addEventListener("resize", handleResize);
      })
      .catch((err) => {
        console.error("ECharts load failed", err);
      });

    return () => {
      charts.forEach((chart) => chart.dispose());
      if (handleResize) window.removeEventListener("resize", handleResize);
    };
  }, [
    isExpanded,
    financials,
    industryReport,
    profitabilitySeries,
    revenueSeries,
    activityIndexSeries,
    industryRentabilitySeries,
    companyAssetsDebtSeries,
    industryAssetsDebtSeries,
  ]);
  
useEffect(() => {
  // Реагируем только если родитель принудительно просит открыть (true)
  // Если родитель передает false, мы позволяем локальному стейту жить своей жизнью
  if (expandRequested) {
    setIsExpanded(true);
  }
}, [expandRequested]);

useEffect(() => {
  if (onExpandedChange) {
    onExpandedChange(isExpanded);
  }
}, [isExpanded]);

  if (status === "loading") {
    return (
      <section className="landing__section landing__snapshot">
        <h2 style={{ textAlign: "center" }}>Ukázka kompletní analýzy</h2>
        <p style={{ textAlign: "center" }}>Načítám fixní snapshot dat…</p>
      </section>
    );
  }

  if (status === "error" || !contextData) {
    return (
      <section className="landing__section landing__snapshot">
        <h2 style={{ textAlign: "center" }}>Ukázka kompletní analýzy</h2>
        <p style={{ textAlign: "center" }}>Snapshot dat se nepodařilo načíst.</p>
      </section>
    );
  }

  return (
    <section className="landing__section landing__snapshot" id="landing-snapshot">


      <div className="landing__snapshot-header">
        <div>
          <h2>Ukázka kompletní analýzy</h2>
          <p>
            Rychlý náhled toho nejdůležitějšího - oceňování, benchmarking i klíčové ukazatele na jednom místě.
          </p>
        </div>
        <div className="landing__snapshot-cta">
          <button
            className="landing__snapshot-button"
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
            aria-controls="landing-snapshot-report"
          >
            {isExpanded ? "Skrýt ukázku reportu" : "Zobrazit celý report"}
          </button>
          <span className="landing__snapshot-note">Bez registrace - jen ukázka dat.</span>
        </div>
      </div>

      {isExpanded && (
        <div className="company-search__context-grid company-search__report" id="landing-snapshot-report">
        <div className="company-search__context-block company-search__context-block--full">
          <div className="company-search__report-header">
            <h3>Souhrn o firmě</h3>
          </div>
          <div className="company-search__summary">
            <div>
              <span>Název</span>
              <strong>{contextData.zakladni_udaje?.nazev || "-"}</strong>
            </div>
            <div>
              <span>IČO</span>
              <strong>{contextData.zakladni_udaje?.ico_str || contextData.zakladni_udaje?.ico || "-"}</strong>
            </div>
            <div>
              <span>Obor</span>
              <strong>{contextData.zakladni_udaje?.obor || "-"}</strong>
            </div>
            <div>
              <span>Zaměstnanci</span>
              <strong>{contextData.zakladni_udaje?.velikost || "-"}</strong>
            </div>
          </div>
          {summaryMetrics && (
            <div className="company-search__summary-metrics">
              <div className="company-search__metric-card">
                <span>Tržby ({summaryMetrics.latestYear})</span>
                <strong>{formatMilKcFromThousands(summaryMetrics.revenueLatest)}</strong>
              </div>
              <div className="company-search__metric-card">
                <span>EBITDA ({summaryMetrics.latestYear})</span>
                <strong>{formatMilKcFromThousands(summaryMetrics.ebitdaLatest)}</strong>
              </div>
              <div className="company-search__metric-card">
                <span>Zisk po zdanění</span>
                <strong>{formatMilKcFromThousands(summaryMetrics.profitLatest)}</strong>
              </div>
              <div className="company-search__metric-card">
                <span>Růst tržeb (CAGR)</span>
                <strong>
                  {Number.isFinite(summaryMetrics.revenueCagr)
                    ? `${(summaryMetrics.revenueCagr * 100).toFixed(1)}%`
                    : "-"}
                </strong>
              </div>
              <div className="company-search__metric-card company-search__metric-card--wide company-search__metric-card--highlight">
                <span>Odhad hodnoty společnosti</span>
                <strong>{valuationRangeText}</strong>
              </div>
            </div>
          )}
          {summaryText && (
            <div className="company-search__summary-text">
              <div className="company-search__markdown">
                <ReactMarkdown
                  components={{
                    p: ({ node, ...props }) => <p className="company-search__markdown-paragraph" {...props} />,
                    strong: ({ node, ...props }) => <strong className="company-search__markdown-strong" {...props} />,
                    ul: ({ node, ...props }) => <ul className="company-search__markdown-list" {...props} />,
                    li: ({ node, ...props }) => <li className="company-search__markdown-list-item" {...props} />,
                  }}
                >
                  {summaryText}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        <div className="company-search__context-block company-search__context-block--full">
          <details className="company-search__details" open>
            <summary>Doplňující informace</summary>
            {additionalInfoRows.length ? (
              <div className="company-search__table-wrapper">
                <table className="company-search__table company-search__table--compact">
                  <tbody>
                    {additionalInfoRows.map((row) => (
                      <tr key={row.label}>
                        <th>{row.label}</th>
                        <td>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Doplňující informace nejsou dostupné.</p>
            )}
          </details>
        </div>

        <div className="company-search__context-block company-search__context-block--full">
          <details className="company-search__details" open>
            <summary>Finanční data</summary>
            {financialTableRows.length ? (
              <div className="company-search__table-wrapper company-search__table-wrapper--wide">
                <table className="company-search__table company-search__table--compact">
                  <thead>
                    <tr>
                      <th>Rok</th>
                      <th>Tržby</th>
                      <th>EBITDA</th>
                      <th>Zisk po zdanění</th>
                      <th>Aktiva celkem</th>
                      <th>ROA</th>
                      <th>ROE</th>
                      <th>EBITDA marže</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financialTableRows.map((row) => (
                      <tr key={row.Rok}>
                        <td>{row.Rok}</td>
                        <td>{formatCurrency(row["Tržby"])}</td>
                        <td>{formatCurrency(row["EBITDA"])}</td>
                        <td>{formatCurrency(row["Zisk po zdanění"])}</td>
                        <td>{formatCurrency(row["Celková aktiva"])}</td>
                        <td>{formatPercent(normalizePercent(row["ROA (%)"]))}</td>
                        <td>{formatPercent(normalizePercent(row["ROE (%)"]))}</td>
                        <td>{formatPercent(normalizePercent(row["EBITDA marže (%)"]))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Finanční data nejsou k dispozici.</p>
            )}
          </details>
        </div>

        <section className="card-grid company-search__context-block--full">
          <div className="card">
            <div className="card__header">
              <h4>Vývoj tržeb a zisku firmy (tis. Kč)</h4>
            </div>
            <div className="chart" ref={revenueChartRef} />
          </div>
          <div className="card">
            <div className="card__header">
              <h4>Indexy medianů odvětví</h4>
              {activityIndexSeries?.window && <span className="badge badge--ghost">{activityIndexSeries.window}</span>}
            </div>
            <div className="chart" ref={activityIndexRef} />
          </div>
        </section>

        <section className="card-grid company-search__context-block--full">
          <div className="card">
            <div className="card__header">
              <h4>Rentabilita firmy v čase</h4>
            </div>
            <div className="chart" ref={profitabilityChartRef} />
          </div>
          <div className="card">
            <div className="card__header">
              <h4>Rentabilita odvětví</h4>
            </div>
            <div className="chart" ref={industryRentabilityRef} />
          </div>
        </section>

        <section className="card-grid company-search__context-block--full">
          <div className="card">
            <div className="card__header">
              <h4>Aktiva a cizí zdroje firmy (tis. Kč)</h4>
            </div>
            <div className="chart" ref={companyAssetsChartRef} />
          </div>
          <div className="card">
            <div className="card__header">
              <h4>Aktiva a zadluženost odvětví</h4>
              {industryAssetsDebtSeries?.window && (
                <span className="badge badge--ghost">{industryAssetsDebtSeries.window}</span>
              )}
            </div>
            <div className="chart" ref={industryAssetsChartRef} />
          </div>
        </section>

        <div className="company-search__context-block company-search__context-block--full">
          <h3>Benchmarking {benchYearsLabel}</h3>
          <p>
            Percentil ukazuje relevantní pozici firmy v oboru. X% firem v tomto ukazateli má horší výsledky (obor se
            hodnotil za období 2023–2025).
          </p>
          {benchmarkRows.length > 0 ? (
            <div className="company-search__table-wrapper">
              <table className="company-search__table">
                <thead>
                  <tr>
                    <th>Metrika</th>
                    <th>Firma</th>
                    <th>Medián</th>
                    <th>Percentil</th>
                    <th>Hodnocení</th>
                  </tr>
                </thead>
                <tbody>
                  {benchmarkRows.map((row) => {
                    const percentileValue = normalizePercentile(row.percentile);
                    const percentileClass = getPercentileClass(percentileValue);
                    return (
                      <tr key={row.metric} className={percentileClass}>
                        <td>{row.metric}</td>
                        <td>{formatBenchmarkValue(row.metric, row.companyValue)}</td>
                        <td>{formatBenchmarkValue(row.metric, row.median)}</td>
                        <td>
                          {Number.isFinite(percentileValue) ? `${percentileValue.toFixed(1)}%` : row.percentile ?? "-"}
                        </td>
                        <td>{row.text || row.status || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p>Data benchmarku nejsou k dispozici.</p>
          )}
        </div>
        </div>
      )}
    </section>
  );
};

export default CompanyReportSnapshot;
